import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search,
  X,
  Loader2,
  Trash2,
  History as HistoryIcon,
  Pencil,
  Plus,
} from 'lucide-react';
import {
  fetchTimeEntries,
  formatUsd,
  deleteTimeEntry,
} from '@/lib/timeEntriesData';
import { useCrewRoster } from '@/lib/crewRoster';
import { useAuth } from '@/context/AuthContext';
import EditTimeEntryDialog from '@/components/clock/EditTimeEntryDialog';
import { toast } from 'sonner';

export const HistoryTable = ({ refreshKey = 0, jobs = [], onChange }) => {
  const { t } = useTranslation();
  const { isOwner } = useAuth();
  // Use the FULL roster (active + inactive) here — historical entries may
  // reference now-inactive employees, and admins need to be able to filter
  // by them.
  const { allRoster: crewRoster } = useCrewRoster();
  const [crewId, setCrewId] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [internalRefresh, setInternalRefresh] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('edit');
  const [dialogEntry, setDialogEntry] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchTimeEntries({
      crewId: crewId === 'all' ? null : crewId,
      fromDate: from || null,
      toDate: to || null,
      search,
    })
      .then((data) => {
        if (alive) setEntries(data);
      })
      .catch(() => {
        if (alive) setEntries([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [crewId, from, to, search, refreshKey, internalRefresh]);

  const totals = useMemo(() => {
    const h = entries.reduce((s, e) => s + (Number(e?.hours) || 0), 0);
    const c = entries.reduce((s, e) => s + (Number(e?.cost) || 0), 0);
    return { h, c, n: entries.length };
  }, [entries]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await deleteTimeEntry(confirmDelete.id);
      setEntries((es) => es.filter((e) => e.id !== confirmDelete.id));
      toast.success(t('crewClock.today.entry_deleted'));
      setConfirmDelete(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.message || t('crewClock.today.delete_failed'));
    } finally {
      setDeletingId(null);
    }
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    setDialogEntry(null);
    setDialogOpen(true);
  };
  const openEditDialog = (entry) => {
    setDialogMode('edit');
    setDialogEntry(entry);
    setDialogOpen(true);
  };
  const handleSaved = () => {
    setInternalRefresh((k) => k + 1);
    onChange?.();
  };

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <HistoryIcon size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('crewClock.history_title')}
            </div>
            <span className="text-xs text-muted-foreground ml-1">
              {t('crewClock.entries.count', {
                count: totals.n,
                hours: totals.h.toFixed(2),
                cost: formatUsd(totals.c),
              })}
            </span>
          </div>
          {isOwner ? (
            <Button
              size="sm"
              variant="outline"
              onClick={openCreateDialog}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs shrink-0"
              data-testid="history-add-entry"
            >
              <Plus size={12} className="mr-1.5" />
              {t('crewClock.add_manual_entry')}
            </Button>
          ) : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select value={crewId} onValueChange={setCrewId}>
            <SelectTrigger
              className="h-10 bg-[hsl(var(--muted))] border-border"
              data-testid="history-filter-crew"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-border">
              <SelectItem value="all">{t('crewClock.history.all_crew')}</SelectItem>
              {crewRoster.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="history-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="history-filter-to"
          />
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('crewClock.history.search_placeholder')}
              className="h-10 pl-9 pr-9 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60"
              data-testid="history-search"
            />
            {search ? (
              <button
                type="button"
                aria-label={t('common.clear_search')}
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-3 rounded-md border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('crewClock.history.col_date')}
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('crewClock.history.col_crew')}
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('crewClock.history.col_job')}
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('crewClock.history.col_hours')}
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {t('crewClock.history.col_cost')}
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground w-20">
                  &nbsp;
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                    {t('crewClock.history.loading')}
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    {t('crewClock.history.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((e) => (
                  <TableRow
                    key={e.id}
                    className="hover:bg-[hsl(var(--muted))]/40 border-border"
                    data-testid={`history-row-${e.id}`}
                  >
                    <TableCell className="text-sm text-foreground">{e.date}</TableCell>
                    <TableCell className="text-sm text-foreground">
                      <Badge
                        variant="outline"
                        className="uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--muted))] text-foreground border-border"
                      >
                        {e.name}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-foreground max-w-[260px] truncate">
                      {e.job}
                    </TableCell>
                    <TableCell className="text-right text-sm text-foreground">
                      {Number(e.hours || 0).toFixed(2)}h
                    </TableCell>
                    <TableCell className="text-right text-sm text-[hsl(var(--success))] font-semibold">
                      {formatUsd(e.cost)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-0.5 justify-end">
                        {isOwner ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEditDialog(e)}
                            className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] h-7 w-7"
                            aria-label={t('crewClock.history.edit_aria')}
                            data-testid={`history-edit-${e.id}`}
                          >
                            <Pencil size={13} />
                          </Button>
                        ) : null}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirmDelete(e)}
                          disabled={deletingId === e.id}
                          className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7"
                          aria-label={t('crewClock.history.delete_aria')}
                          data-testid={`history-delete-${e.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="history-delete-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('crewClock.history.delete_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {confirmDelete
                ? t('crewClock.history.delete_description', {
                    name: confirmDelete.name,
                    date: confirmDelete.date,
                    hours: Number(confirmDelete.hours || 0).toFixed(2),
                  })
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
              disabled={!!deletingId}
            >
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!!deletingId}
              className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
              data-testid="history-delete-confirm-action"
            >
              {deletingId ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {t('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isOwner ? (
        <EditTimeEntryDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          entry={dialogEntry}
          jobs={jobs}
          onSaved={handleSaved}
        />
      ) : null}
    </Card>
  );
};

export default HistoryTable;
